import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { 
  PlusIcon, 
  PencilIcon,
  TrashIcon,
  CalendarDaysIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const SeasonsContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  padding: 2rem;
`;

const SeasonsHeader = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: ${theme.shadows.sm};
  border: 1px solid ${theme.colors.border.primary};
`;

const SeasonsTitle = styled.h1`
  font-size: ${theme.fontSizes['3xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const SeasonsSubtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.lg};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const CreateSeasonButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.primary.dark};
    transform: translateY(-1px);
  }
`;

const SeasonsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const SeasonCard = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    box-shadow: ${theme.shadows.md};
    transform: translateY(-2px);
  }
  
  ${props => props.active && `
    border-color: ${theme.colors.success.main};
    background: ${theme.colors.success.light};
  `}
`;

const SeasonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const SeasonName = styled.h3`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const SeasonActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.main};
  }
  
  &.danger:hover {
    border-color: ${theme.colors.error.main};
    color: ${theme.colors.error.main};
  }
`;

const SeasonDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SeasonDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const SeasonStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${props => props.active ? `
    background: ${theme.colors.success.light};
    color: ${theme.colors.success.dark};
  ` : `
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.secondary};
  `}
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  
  &:hover {
    color: ${theme.colors.text.primary};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px ${theme.colors.primary.light};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 1rem;
  height: 1rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.primary {
    background: ${theme.colors.primary.main};
    color: white;
    border: none;
    
    &:hover {
      background: ${theme.colors.primary.dark};
    }
  }
  
  &.secondary {
    background: transparent;
    color: ${theme.colors.text.secondary};
    border: 1px solid ${theme.colors.border.primary};
    
    &:hover {
      border-color: ${theme.colors.text.primary};
      color: ${theme.colors.text.primary};
    }
  }
`;

const SeasonsPage = () => {
  const { isAuthenticated } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    year: new Date().getFullYear(),
    start_date: '',
    end_date: '',
    is_active: false
  });

  useEffect(() => {
    if (isAuthenticated) {
      loadSeasons();
    }
  }, [isAuthenticated]);

  const loadSeasons = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/seasons');
      setSeasons(response.data.seasons || []);
    } catch (error) {
      console.error('Error loading seasons:', error);
      toast.error('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSeason = () => {
    setEditingSeason(null);
    setFormData({
      name: '',
      year: new Date().getFullYear(),
      start_date: '',
      end_date: '',
      is_active: false
    });
    setShowModal(true);
  };

  const handleEditSeason = (season) => {
    setEditingSeason(season);
    setFormData({
      name: season.name,
      year: season.year,
      start_date: season.start_date,
      end_date: season.end_date,
      is_active: season.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSeason) {
        await api.put(`/api/seasons/${editingSeason.id}`, formData);
        toast.success('Season updated successfully');
      } else {
        await api.post('/api/seasons', formData);
        toast.success('Season created successfully');
      }
      
      setShowModal(false);
      loadSeasons();
    } catch (error) {
      console.error('Error saving season:', error);
      toast.error('Failed to save season');
    }
  };

  const handleDeleteSeason = async (season) => {
    if (!window.confirm(`Are you sure you want to delete "${season.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/api/seasons/${season.id}`);
      toast.success('Season deleted successfully');
      loadSeasons();
    } catch (error) {
      console.error('Error deleting season:', error);
      toast.error('Failed to delete season');
    }
  };

  const handleActivateSeason = async (season) => {
    try {
      await api.post(`/api/seasons/${season.id}/activate`);
      toast.success(`"${season.name}" activated`);
      loadSeasons();
    } catch (error) {
      console.error('Error activating season:', error);
      toast.error('Failed to activate season');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isAuthenticated) {
    return <div>Please log in to view seasons.</div>;
  }

  return (
    <SeasonsContainer>
      <SeasonsHeader>
        <SeasonsTitle>Season Management</SeasonsTitle>
        <SeasonsSubtitle>
          Create and manage boating seasons for trip reporting and statistics
        </SeasonsSubtitle>
        <HeaderActions>
          <CreateSeasonButton onClick={handleCreateSeason}>
            <PlusIcon className="w-5 h-5" />
            Create Season
          </CreateSeasonButton>
        </HeaderActions>
      </SeasonsHeader>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: theme.colors.text.secondary }}>
          Loading seasons...
        </div>
      ) : (
        <SeasonsList>
          {seasons.map((season) => (
            <SeasonCard key={season.id} active={season.is_active}>
              <SeasonHeader>
                <SeasonName>{season.name}</SeasonName>
                <SeasonActions>
                  <ActionButton onClick={() => handleEditSeason(season)} title="Edit Season">
                    <PencilIcon className="w-4 h-4" />
                  </ActionButton>
                  <ActionButton 
                    className="danger" 
                    onClick={() => handleDeleteSeason(season)}
                    title="Delete Season"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </ActionButton>
                </SeasonActions>
              </SeasonHeader>
              
              <SeasonDetails>
                <SeasonDetail>
                  <CalendarDaysIcon className="w-4 h-4" />
                  {formatDate(season.start_date)} - {formatDate(season.end_date)}
                </SeasonDetail>
                <SeasonDetail>
                  Year: {season.year}
                </SeasonDetail>
              </SeasonDetails>
              
              <SeasonStatus active={season.is_active}>
                {season.is_active ? (
                  <>
                    <CheckCircleIcon className="w-4 h-4" />
                    Active
                  </>
                ) : (
                  'Inactive'
                )}
              </SeasonStatus>
              
              {!season.is_active && (
                <Button 
                  className="primary" 
                  onClick={() => handleActivateSeason(season)}
                  style={{ marginTop: '1rem', width: '100%' }}
                >
                  Activate Season
                </Button>
              )}
            </SeasonCard>
          ))}
        </SeasonsList>
      )}

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingSeason ? 'Edit Season' : 'Create Season'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Season Name</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., 2024 Boating Season"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Year</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  min="2020"
                  max="2030"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <CheckboxGroup>
                  <Checkbox
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <Label>Set as active season</Label>
                </CheckboxGroup>
              </FormGroup>
              
              <ModalActions>
                <Button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="primary">
                  {editingSeason ? 'Update Season' : 'Create Season'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </SeasonsContainer>
  );
};

export default SeasonsPage;
